import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API, useAuth } from "../../App";
import TodaysActions from "../../components/admin/TodaysActions";
import {
  Users, Mail, TrendingUp, Building,
  BarChart3, Target, Eye,
  MousePointerClick, Timer, ShieldAlert, ShieldCheck
} from "lucide-react";

const Dashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [emailAnalytics, setEmailAnalytics] = useState(null);
  const [clpStats, setClpStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, analyticsRes, clpRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers }),
        axios.get(`${API}/email-analytics`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/clp/attribution-stats`, { headers }).catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setEmailAnalytics(analyticsRes.data);
      setClpStats(clpRes.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A2342]"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: "Total Leads",
      value: stats?.total_leads || 0,
      icon: Users,
      color: "bg-blue-500",
      change: `+${stats?.recent_leads_7d || 0} this week`
    },
    {
      title: "Conversion Rate",
      value: `${stats?.conversion_rate || 0}%`,
      icon: Target,
      color: "bg-green-500",
      change: "Lead → Converted"
    },
    {
      title: "Emails Sent",
      value: stats?.campaigns?.emails_sent || 0,
      icon: Mail,
      color: "bg-purple-500",
      change: `${stats?.campaigns?.total || 0} campaigns`
    },
    {
      title: "Active Deals",
      value: (stats?.deals?.specials || 0) + (stats?.deals?.off_market || 0),
      icon: Building,
      color: "bg-orange-500",
      change: `${stats?.deals?.specials || 0} specials, ${stats?.deals?.off_market || 0} off-market`
    }
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#0A2342] font-['Playfair_Display']" data-testid="dashboard-title">
          Dashboard
        </h1>
        <p className="text-slate-600">Welcome back! Here's your lead generation overview.</p>
      </div>

      {/* Phase B — Today's Actions tray (above existing cards) */}
      <TodaysActions />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl p-6 border border-slate-200 hover:shadow-lg transition-shadow"
            data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">{stat.title}</p>
                <h3 className="text-3xl font-bold text-[#0A2342]">{stat.value}</h3>
                <p className="text-xs text-slate-400 mt-2">{stat.change}</p>
              </div>
              <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Lead Distribution */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* By Type */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 border border-slate-200"
          data-testid="leads-by-type"
        >
          <h3 className="text-lg font-semibold text-[#0A2342] mb-4">Leads by Type</h3>
          <div className="space-y-4">
            {[
              { label: "Renters", value: stats?.leads_by_type?.renters || 0, color: "bg-blue-500" },
              { label: "Buyers", value: stats?.leads_by_type?.buyers || 0, color: "bg-green-500" },
              { label: "Investors", value: stats?.leads_by_type?.investors || 0, color: "bg-purple-500" }
            ].map((item) => (
              <div key={item.label} className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className={`w-3 h-3 ${item.color} rounded-full mr-3`} />
                  <span className="text-slate-600">{item.label}</span>
                </div>
                <span className="font-semibold text-[#0A2342]">{item.value}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* By Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl p-6 border border-slate-200"
          data-testid="leads-by-status"
        >
          <h3 className="text-lg font-semibold text-[#0A2342] mb-4">Leads by Status</h3>
          <div className="space-y-4">
            {[
              { label: "New", value: stats?.leads_by_status?.new || 0, color: "bg-yellow-500" },
              { label: "Contacted", value: stats?.leads_by_status?.contacted || 0, color: "bg-blue-500" },
              { label: "Touring", value: stats?.leads_by_status?.touring || 0, color: "bg-orange-500" },
              { label: "Converted", value: stats?.leads_by_status?.converted || 0, color: "bg-green-500" }
            ].map((item) => (
              <div key={item.label} className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className={`w-3 h-3 ${item.color} rounded-full mr-3`} />
                  <span className="text-slate-600">{item.label}</span>
                </div>
                <span className="font-semibold text-[#0A2342]">{item.value}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Commission Lock Protocol */}
      {clpStats && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-slate-200 overflow-hidden"
          data-testid="clp-overview"
        >
          <div className="p-6 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-[#DE6E4B]" /> Commission Attribution
              </h3>
              <p className="text-sm text-slate-500 mt-0.5">Protect your referral credit</p>
            </div>
            <a href="/admin/attribution" className="text-sm text-[#DE6E4B] hover:underline font-medium" data-testid="clp-view-all">
              View Details →
            </a>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6">
            <div className="text-center p-3 bg-green-50 rounded-xl">
              <ShieldCheck className="w-6 h-6 text-green-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-green-600">{clpStats.acknowledged}</p>
              <p className="text-xs text-slate-500">Protected</p>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-xl">
              <ShieldAlert className="w-6 h-6 text-red-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-red-600">{clpStats.touring_without_ack}</p>
              <p className="text-xs text-slate-500">At Risk</p>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-xl">
              <TrendingUp className="w-6 h-6 text-blue-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-blue-600">{clpStats.high_intent}</p>
              <p className="text-xs text-slate-500">High Intent</p>
            </div>
            <div className="text-center p-3 bg-slate-50 rounded-xl">
              <Target className="w-6 h-6 text-[#DE6E4B] mx-auto mb-1" />
              <p className="text-2xl font-bold text-[#0A2342]">{clpStats.acknowledgment_rate}%</p>
              <p className="text-xs text-slate-500">Ack Rate</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[#0A2342] rounded-xl p-6 text-white"
        data-testid="quick-actions"
      >
        <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <a
            href="/admin/leads"
            className="bg-white/10 rounded-lg p-4 hover:bg-white/20 transition-colors text-center"
            data-testid="quick-action-leads"
          >
            <Users className="w-6 h-6 mx-auto mb-2" />
            <span className="text-sm">View Leads</span>
          </a>
          <a
            href="/admin/campaigns"
            className="bg-white/10 rounded-lg p-4 hover:bg-white/20 transition-colors text-center"
            data-testid="quick-action-campaigns"
          >
            <Mail className="w-6 h-6 mx-auto mb-2" />
            <span className="text-sm">Send Email</span>
          </a>
          <a
            href="/admin/templates"
            className="bg-white/10 rounded-lg p-4 hover:bg-white/20 transition-colors text-center"
            data-testid="quick-action-templates"
          >
            <BarChart3 className="w-6 h-6 mx-auto mb-2" />
            <span className="text-sm">Templates</span>
          </a>
          <a
            href="/admin/content"
            className="bg-white/10 rounded-lg p-4 hover:bg-white/20 transition-colors text-center"
            data-testid="quick-action-content"
          >
            <Building className="w-6 h-6 mx-auto mb-2" />
            <span className="text-sm">Manage Content</span>
          </a>
        </div>
      </motion.div>

      {/* Email Analytics */}
      {emailAnalytics && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-slate-200"
          data-testid="email-analytics"
        >
          <div className="p-6 border-b border-slate-200">
            <h3 className="text-lg font-semibold text-[#0A2342]">Email Performance</h3>
            <p className="text-sm text-slate-500 mt-1">Tracking opens, clicks, and engagement</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-6">
            <div className="text-center p-4 bg-slate-50 rounded-xl">
              <Mail className="w-6 h-6 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-[#0A2342]">{emailAnalytics.emails_sent}</p>
              <p className="text-xs text-slate-500">Emails Sent</p>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-xl">
              <Eye className="w-6 h-6 text-green-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-[#0A2342]">{emailAnalytics.open_rate}%</p>
              <p className="text-xs text-slate-500">Open Rate</p>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-xl">
              <MousePointerClick className="w-6 h-6 text-purple-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-[#0A2342]">{emailAnalytics.click_rate}%</p>
              <p className="text-xs text-slate-500">Click Rate</p>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-xl">
              <Timer className="w-6 h-6 text-amber-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-[#0A2342]">{stats?.follow_ups?.scheduled || 0}</p>
              <p className="text-xs text-slate-500">Drip Scheduled</p>
            </div>
            <div className="text-center p-4 bg-slate-50 rounded-xl">
              <Target className="w-6 h-6 text-[#DE6E4B] mx-auto mb-2" />
              <p className="text-2xl font-bold text-[#0A2342]">{stats?.follow_ups?.sent || 0}</p>
              <p className="text-xs text-slate-500">Drip Sent</p>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default Dashboard;
